import { useFormatAmount, useIntl } from '@capsicum/common';
import { Text } from '@capsicum/common-web';
import { type BidStatusCardProps } from '@capsicum/features/fanshop/shared';
import styled from 'styled-components';

export const BidStatusCard = ({
	chzRate,
	chzShortCode,
	fiatShortCode,
	isActive = false,
	isOwnBid = true,
	priceCurrentBid,
	priceCurrentBidChz,
	priceNextBid
}: BidStatusCardProps) => {
	const { $t } = useIntl();
	const { formatAmount } = useFormatAmount();

	return (
		<S.Layout $isOwnBid={isOwnBid}>
			<S.CurrentBid>
				<S.CurrentBidLayout $isOwnBid={isOwnBid}>
					<S.CurrentBidTextLayout>
						<S.CurrentBidText variant="title6">
							{isActive
								? $t('rewards.auctions.status.currentBid')
								: $t('rewards.auctions.status.winning')}
						</S.CurrentBidText>
					</S.CurrentBidTextLayout>
					{isOwnBid && (
						<S.CurrentBidTextLayout>
							<S.YourBidText variant="caption1">{$t('rewards.auctions.status.yourBid')}</S.YourBidText>
						</S.CurrentBidTextLayout>
					)}
				</S.CurrentBidLayout>
				<S.BidPriceLayout>
					<S.CurrentBidPrice variant="title5">
						{`${formatAmount(priceCurrentBid, {
							maximumFractionDigits: 2
						})} ${fiatShortCode}`}
					</S.CurrentBidPrice>
					<S.BidPriceCHZ variant="caption2">
						{`(${formatAmount(priceCurrentBidChz, {
							maximumFractionDigits: 2,
							minimumFractionDigits: 2
						})} ${chzShortCode})`}
					</S.BidPriceCHZ>
				</S.BidPriceLayout>
			</S.CurrentBid>
			{isActive && priceNextBid ? (
				<S.NextBid>
					<S.NextBidText variant="text1">{$t('rewards.auctions.status.nextMinimumBid')}</S.NextBidText>
					<S.BidPriceLayout>
						<S.NextBidPrice variant="text1">
							{`${formatAmount(priceNextBid, {
								maximumFractionDigits: 2
							})} ${fiatShortCode}`}
						</S.NextBidPrice>
						<S.BidPriceCHZ variant="caption2">
							{`(${formatAmount(priceNextBid! / chzRate, {
								maximumFractionDigits: 2,
								minimumFractionDigits: 2
							})} ${chzShortCode})`}
						</S.BidPriceCHZ>
					</S.BidPriceLayout>
				</S.NextBid>
			) : null}
		</S.Layout>
	);
};

namespace S {
	export const Layout = styled.div<{ $isOwnBid: boolean }>`
		background-color: ${({ $isOwnBid, theme }) =>
			$isOwnBid ? theme.colors.backgrounds.brand.selected : theme.colors.backgrounds.main.screen};
		padding: ${({ theme }) => theme.spacings.small};
		${({ theme }) => theme.border.radius.medium};
		border: 1px solid
			${({ $isOwnBid, theme }) =>
				$isOwnBid ? theme.colors.borders.brand.selected : theme.colors.borders.main.divider};
	`;

	export const CurrentBid = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		margin-bottom: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const CurrentBidLayout = styled.div<{ $isOwnBid: boolean }>`
		justify-content: ${({ $isOwnBid }) => ($isOwnBid ? 'space-between' : 'center')};
		height: 37px;
	`;

	export const CurrentBidTextLayout = styled.div`
		display: flex;
		flex-direction: row;
		align-items: center;
	`;

	export const CurrentBidText = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const YourBidText = styled(Text)`
		${({ theme }) => theme.fonts.text.caption1};
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		text-transform: uppercase;
	`;

	export const CurrentBidPrice = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		text-align: right;
	`;

	export const NextBid = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
	`;

	export const NextBidText = styled(Text)`
		${({ theme }) => theme.fonts.text.caption1};
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;

	export const NextBidPrice = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.subtle};
		text-align: right;
	`;

	export const BidPriceLayout = styled.div`
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	`;

	export const BidPriceCHZ = styled(Text)`
		text-align: right;
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
}
